import { action, runInAction, observable } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import seoController from './controller';
import {
  SeoItemInterface,
  SeoItemListRequestInterface,
  CreateSeoItemRequestInterface,
  UpdateSeoItemRequestInterface,
  SeoPageFaqInterface,
  CreateSeoPageFaqInterface,
  UpdateSeoPageFaqInterface,
  FindSeoPageRequestInterface,
  SeoBlogItemInterface,
  CreateSeoBlogInterface,
  UpdateSeoBlogInterface,
} from '../api/seoReferenceApi';

import {
  ReviewItemInterface, ReviewRequestInterface,
  CreateReviewRequestInterface, UpdateReviewRequestInterface,
} from '../api/reviewReferenceApi';

import { BannerItemInterface, CreateBannerInterface, UpdateBannerInterface } from '../api/bannerReferenceApi';

export interface SeoReferenceStore {
  seoPages: SeoItemInterface[],
  seoPagesRequest: SeoItemListRequestInterface,
  currentSeoPage: SeoItemInterface,
  findSeoPageRequest: FindSeoPageRequestInterface,
  seoPagesTopLevel: SeoItemInterface[],

  seoPageFaqs: SeoPageFaqInterface[],
  seoPageFaqBySeoPageId: number,

  reviews: ReviewItemInterface[],
  reviewRequest: ReviewRequestInterface,
  currentReview: ReviewItemInterface,

  seoBlogs: SeoBlogItemInterface[],
  seoBlogsByServiceId: number,
  currentSeoBlog: SeoBlogItemInterface,

  seoTree: any[],
  selectedTreeNode: any,

  bannersList: BannerItemInterface[],
  currentBanner: BannerItemInterface,

  error: ErrorInterface,
}

const initialValues: SeoReferenceStore = {
  seoPages: [],
  seoPagesRequest: null,
  currentSeoPage: null,
  findSeoPageRequest: null,
  seoPagesTopLevel: [],

  reviews: [],
  reviewRequest: null,
  currentReview: null,

  seoPageFaqs: [],
  seoPageFaqBySeoPageId: null,

  seoBlogs: [],
  seoBlogsByServiceId: null,
  currentSeoBlog: null,

  seoTree: [],
  selectedTreeNode: null,

  bannersList: [],
  currentBanner: null,

  error: null,
};

export const seoReferenceStore = observable(initialValues);

export const seoReferenceActions = {

  async getSeoPages(request :SeoItemListRequestInterface) {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.seoPagesRequest = request;
    });
    const response = await seoController.getSeoPages(request);
    if (response.errorData) {
      seoReferenceStore.seoPagesRequest = null;
    }
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.seoPages = response.data;
      if (!request.parentId) {
        seoReferenceStore.seoPagesTopLevel = response.data;
      };
    });
  },

  async findSeoPages(request :FindSeoPageRequestInterface) {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.findSeoPageRequest = request;
    });
    const response = await seoController.findSeoPages(request);
    if (response.errorData) {
      seoReferenceStore.seoPagesRequest = null;
    }
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.seoPages = response.data;
    });
  },

  handleError: action((response, id:string = null) => {
    if (response.errorData) {
      const { errorData } = response;
      seoReferenceStore.error = {
        message: errorData?.message,
        details: errorData?.details,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
        id,
      };
      throw errorData;
    }
  }),

  handleErrorWithoutThrow: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      seoReferenceStore.error = errorData;
    }
  }),

  createSeoPage: async (request: CreateSeoItemRequestInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.createSeoPage(request);
    seoReferenceActions.handleError(response);
  },

  updateSeoPage: async (request: UpdateSeoItemRequestInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.updateSeoPage(request);
    seoReferenceActions.handleError(response);
  },

  deleteSeoPage: async (id: number): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deleteSeoPage(id);
    seoReferenceActions.handleError(response);
  },

  reindexSeoPage: async (id: number): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.reindexSeoPage(id);
    seoReferenceActions.handleError(response);
  },

  clearSeoPages: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.seoPages = [];
        seoReferenceStore.seoPagesRequest = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  clearFindSeoPageRequest: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.findSeoPageRequest = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  // Возвращает страницу по id, не затрагивая seoPages/findSeoPageRequest (состояние таблицы)
  findSeoPageById: async (
    serviceId: number,
    pageId: number,
    phrase: string
  ): Promise<SeoItemInterface | null> => {
    seoReferenceActions.clearError();
    const response = await seoController.findSeoPages({ serviceId, phrase });
    seoReferenceActions.handleError(response);
    return (response.data ?? []).find((page: SeoItemInterface) => page.id === pageId) ?? null;
  },

  setCurrentSeoPage: async (data: SeoItemInterface): Promise<void> => {
    seoReferenceActions.clearError();
    seoReferenceActions.clearSeoPageFaq();    
    runInAction(() => {
      seoReferenceStore.currentSeoPage = data;
    });
  },

  clearCurrentSeoPage: async (): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentSeoPage = null;
    });
  },

  clearError: action(() => {
    seoReferenceStore.error = null;
  }),

  async getReviews(request :ReviewRequestInterface) {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.reviewRequest = request;
    });
    const response = await seoController.getReviews(request);
    if (response.errorData) {
      seoReferenceStore.reviewRequest = null;
    }
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.reviews = response.data;
    });
  },

  createReview: async (request: CreateReviewRequestInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.createReview(request);
    seoReferenceActions.handleError(response);
  },

  updateReview: async (request: UpdateReviewRequestInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.updateReview(request);
    seoReferenceActions.handleError(response);
  },

  deleteReview: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deleteReview(id);
    seoReferenceActions.handleError(response);
  },

  restoreReview: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.restoreReview(id);
    seoReferenceActions.handleError(response);
  },

  setCurrentReview: async (data: ReviewItemInterface): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentReview = data;
    });
  },

  clearCurrentReview: async (): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentReview = null;
    });
  },

  clearReviews: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.reviews = [];
        seoReferenceStore.reviewRequest = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  // SEQ FAQ блок
  async getSeoPageFaqs(seoPageId :number) {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.seoPageFaqBySeoPageId = seoPageId;
    });
    const response = await seoController.getSeoPageFaqs(seoPageId);
    if (response.errorData) {
      seoReferenceStore.seoPageFaqBySeoPageId = null;
    }
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.seoPageFaqs = (response.data ?? []).sort((a, b) => a.sort - b.sort);
    });
  },

  addNewSeoPageFaq: async (seoPageId: number): Promise<void> => {
    runInAction(() => {
      seoReferenceStore.seoPageFaqs = [...seoReferenceStore.seoPageFaqs, {
        id: `newId-${ seoReferenceStore.seoPageFaqs.length + 1 }`,
        seoPageId,
        sort: seoReferenceStore.seoPageFaqs.length + 1,
        added: true,
      }];
    });
  },

  updateSeoPageFaqItem: async (seoPageFaq: SeoPageFaqInterface): Promise<void> => {
    runInAction(() => {
      seoReferenceStore.seoPageFaqs = seoReferenceStore.seoPageFaqs.map(
        (v) => {
          if (v.id === seoPageFaq.id) {
            return {
              ...v,
              answer: seoPageFaq.answer ?? v.answer,
              question: seoPageFaq.question ?? v.question,
              sort: seoPageFaq.sort ?? v.sort,
              updated: true,
            };
          }
          return v;
        }
      );
    });
  },

  deleteSeoPageFaqItem: async (seoPageFaqId: string): Promise<void> => {
    runInAction(() => {
      seoReferenceStore.seoPageFaqs = seoReferenceStore.seoPageFaqs.map(
        (v) => {
          if (v.id === seoPageFaqId) {
            return {
              ...v,
              deleted: true,
            };
          }
          return v;
        }
      );
    });
  },

  createSeoPageFaq: async (request: CreateSeoPageFaqInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.createSeoPageFaq(request);
    seoReferenceActions.handleError(response);
  },

  updateSeoPageFaq: async (request: UpdateSeoPageFaqInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.updateSeoPageFaq(request);
    seoReferenceActions.handleError(response);
  },

  deleteSeoPageFaq: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deleteSeoPageFaq(id);
    seoReferenceActions.handleError(response);
  },

  saveSeoPageFaqs: async (): Promise<void> => {
    seoReferenceStore.seoPageFaqs.forEach(
      (v) => {
        if (v.added && !v.deleted) {
          seoReferenceActions.createSeoPageFaq({
            seoPageId: v.seoPageId,
            sort: v.sort,
            answer: v.answer,
            question: v.question,
          });
        }
        if (v.updated && !v.added && !v.deleted) {
          seoReferenceActions.updateSeoPageFaq({
            seoPageId: v.seoPageId,
            sort: v.sort,
            answer: v.answer,
            question: v.question,
            seoPageFaqId: v.id,
          });
        }
        if (v.deleted && !v.added) {
          seoReferenceActions.deleteSeoPageFaq(v.id);
        }
      }
    );
    // seoReferenceActions.getSeoPageFaqs();
  },

  clearSeoPageFaq: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.seoPageFaqs = [];
        seoReferenceStore.seoPageFaqBySeoPageId = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  // SEO BLOG блок
  async getSeoBlogs(serviceId: number) {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.seoBlogsByServiceId = serviceId;
    });
    const response = await seoController.getSeoBlogs(serviceId);
    if (response.errorData) {
      seoReferenceStore.seoBlogsByServiceId = null;
    }
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.seoBlogs = response.data;
    });
  },

  createSeoBlog: async (request: CreateSeoBlogInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.createSeoBlog(request);
    seoReferenceActions.handleError(response);
  },

  updateSeoBlog: async (request: UpdateSeoBlogInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.updateSeoBlog(request);
    seoReferenceActions.handleError(response);
  },

  deleteSeoBlog: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deleteSeoBlog(id);
    seoReferenceActions.handleError(response);
  },

  restoreSeoBlog: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.restoreSeoBlog(id);
    seoReferenceActions.handleError(response);
  },

  setCurrentSeoBlog: async (data: SeoBlogItemInterface): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentSeoBlog = data;
    });
  },

  clearCurrentSeoBlog: async (): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentSeoBlog = null;
    });
  },

  clearSeoBlogs: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.seoBlogs = [];
        seoReferenceStore.seoBlogsByServiceId = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  // SEO Tree блок
  async getSeoTree(request:SeoItemListRequestInterface) {
    seoReferenceActions.clearError();
    const response = await seoController.getSeoTree(request);
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.seoTree = response.data.items;      
    });
  },

  clearSeoTree: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.seoTree = [];
        seoReferenceStore.selectedTreeNode = null;
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  setSelectedTreeNode: async (data: any): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.selectedTreeNode = data;      
    });
  },

  // Banners блок
  async getBannersList() {
    seoReferenceActions.clearError();
    const response = await seoController.getBannersList();
    seoReferenceActions.handleError(response);
    runInAction(() => {
      seoReferenceStore.bannersList = response.data.items;
    });
  },

  clearBannersList: async () => {
    try {
      runInAction(() => {
        seoReferenceStore.bannersList = [];
      });
    } catch (err) {
      seoReferenceActions.handleError(err);
    }
  },

  setCurrentBanner: async (data: BannerItemInterface): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentBanner = data;
    });
  },

  clearCurrentBanner: async (): Promise<void> => {
    seoReferenceActions.clearError();
    runInAction(() => {
      seoReferenceStore.currentBanner = null;
    });
  },

  createBanner: async (request: CreateBannerInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.createBanner(request);
    seoReferenceActions.handleError(response);
  },

  updateBanner: async (request: UpdateBannerInterface): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.updateBanner(request);
    seoReferenceActions.handleError(response);
  },

  deleteBanner: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deleteBanner(id);
    seoReferenceActions.handleError(response);
  },

  activateBanner: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.activateBanner(id);
    seoReferenceActions.handleError(response);
  },

  deactivateBanner: async (id: string): Promise<void> => {
    seoReferenceActions.clearError();
    const response = await seoController.deactivateBanner(id);
    seoReferenceActions.handleError(response);
  },
};
